/**
 * Shared constants and helpers for entity match scoring.
 *
 * Used by entity-match-selector, len-step, sa-step, meter-step, and the store
 * to apply consistent thresholds and score classifications.
 */

/** Matches with a score below this threshold are hidden from the user. */
export const MIN_DISPLAY_SCORE = 0.35;

/**
 * Score at or above which an ENTITY match (customer, LEN, SA, meter) is
 * considered confident enough to auto-apply without asking the user to
 * confirm — used by BOTH initializeSelections (to pre-populate the entity
 * selection) AND analyzeFastTrack (to decide whether the step can be
 * skipped). Keeping them aligned is load-bearing: if the analyzer skipped
 * at a lower threshold than the selection was pre-populated at, fast-track
 * would silently create new entities while ignoring real matches.
 * See guides/ai-onboarding/FAST-TRACK.md.
 *
 * Set at 0.98 so only essentially-identical names auto-link; any partial /
 * subset name match (which the backend NameMatcher caps below 0.98) pauses
 * Express Import and asks the user to verify it's new or link it (DEV-15016).
 */
export const AUTO_ACCEPT_SCORE = 0.98;

/**
 * Score at or above which a SUPPLIER match is considered confident enough
 * to auto-apply. Intentionally lower than AUTO_ACCEPT_SCORE because supplier
 * matching (by supplier-name similarity) is inherently fuzzier than entity
 * matching (which uses a known id or address), and the downside of a wrong
 * supplier link is lighter — the user can correct it in bill-review without
 * corrupting the entity hierarchy. Used by the analyzer's supplier
 * classification and the supplier-confidence warning on the summary step.
 */
export const SUPPLIER_AUTO_ACCEPT_SCORE = 0.80;

/** True when a match's score is high enough to auto-apply without user review. */
export function shouldAutoAcceptMatch(match: { score: number } | null | undefined): boolean {
  return match != null && match.score >= AUTO_ACCEPT_SCORE;
}

/** CSS class applied to score chips based on match confidence. */
export type ScoreClass = 'high' | 'med' | 'low';

/**
 * Returns a ScoreClass for a given score percentage.
 *
 * @param percent - Score as a 0-100 integer.
 */
export function getScoreClass(percent: number): ScoreClass {
  if (percent >= 80) return 'high';
  if (percent >= 50) return 'med';
  return 'low';
}
